import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaHome, FaUsers, FaChartLine, FaHandshake, FaArrowRight,
  FaStar, FaMapMarkerAlt, FaShieldAlt, FaBuilding, FaUserTie, FaMedal,
  FaCheckCircle, FaTimesCircle, FaSearch, FaPhoneAlt,
  FaWhatsapp, FaRegHeart, FaShareAlt, FaVectorSquare,
  FaEye, FaThLarge
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { sendApiRequest } from '../utils';

// Import hero images (adjust paths as needed)
import heroBg1 from '../assets/images/hero-bg1.jpg';
import heroBg2 from '../assets/images/hero-bg2.jpg';
import heroBg3 from '../assets/images/hero-bg3.jpg';
import defaultPlotImage from '../assets/images/logo_new.jpeg';

const Home = () => {
  const heroBackgrounds = [heroBg1, heroBg2, heroBg3];
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatPrice = (price) => {
    if (!price) return "₹0";
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} Lakh`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest("api/packages", {}, "GET");

      if (response.data) {
        const packages = Array.isArray(response.data) ? response.data : [];
        const formattedPlots = packages.map((pkg, index) => ({
          id: pkg._id || index,
          _id: pkg._id,
          number: pkg.plotNumber || `${String.fromCharCode(65 + ((pkg.row || 1) - 1))}${(pkg.col || 1)}`,
          row: (pkg.row || 1) - 1, // Convert from 1-based to 0-based
          col: (pkg.col || 1) - 1, // Convert from 1-based to 0-based
          width: pkg.gridWidth || 1,
          height: pkg.gridHeight || 1,
          size: pkg.sizeCategory || "medium",
          area: `${pkg.width || 30}' x ${pkg.length || 30}' = ${(pkg.width || 30) * (pkg.length || 30)} sq.ft`,
          dimensions: `${pkg.width || 30}' x ${pkg.length || 30}'`,
          price: formatPrice(pkg.price),
          rawPrice: pkg.price,
          status: pkg.plotStatus || "available",
          facing: pkg.facing || "North",
          location: pkg.location || "Prime Location",
          title: pkg.title,
          description: pkg.description || `Beautiful ${pkg.facing || "North"} facing plot available for sale.`,
          features: pkg.features || ['24/7 Security', 'Park', 'Club House'],
          nearby: pkg.nearby || ['Metro Station', 'School', 'Hospital'],
          photos: (pkg.photos && pkg.photos.length > 0)
            ? pkg.photos
            : [defaultPlotImage],
          isCorner: pkg.isCorner || false,
          isPremium: pkg.isPremium || false,
          badge: pkg.isPremium ? "Premium" : pkg.isCorner ? "Corner Plot" : "Prime Location",
          discount: null,
          type: pkg.sizeCategory ? `${pkg.sizeCategory.charAt(0).toUpperCase() + pkg.sizeCategory.slice(1)} Plot` : "Residential Plot",
          widthDim: `${pkg.width || 30} Feet`,
          depthDim: `${pkg.length || 30} Feet`,
        }));
        setPlots(formattedPlots);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto slide hero background
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev === heroBackgrounds.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { number: "1000+", label: "Premium Properties", icon: FaHome },
    { number: "500+", label: "Satisfied Clients", icon: FaUsers },
    { number: "50+", label: "Prime Locations", icon: FaMapMarkerAlt },
    { number: "100Cr+", label: "Total Value", icon: FaChartLine }
  ];

  // Filter properties based on status and search
  const filteredProperties = plots.filter(property => {
    if (filterStatus !== 'all' && property.status !== filterStatus) return false;
    if (searchTerm && !property.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !property.location.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Plot Map Grid Component
  const PlotMapGrid = ({ plots: allPlots }) => {
    const [selectedPlot, setSelectedPlot] = useState(null);
    const [mapFilter, setMapFilter] = useState('all');

    const getStatusColor = (status) => {
      switch (status) {
        case 'available': return 'bg-green-500 hover:bg-green-600';
        case 'sold': return 'bg-red-500 hover:bg-red-600';
        case 'booked': return 'bg-yellow-500 hover:bg-yellow-600';
        default: return 'bg-gray-400 hover:bg-gray-500';
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case 'available': return 'Available';
        case 'sold': return 'Sold';
        case 'booked': return 'Booked';
        default: return 'N/A';
      }
    };

    const filteredMapPlots = mapFilter === 'all' ? allPlots : allPlots.filter(plot => plot.status === mapFilter);

    // Calculate statistics
    const availableCount = allPlots.filter(p => p.status === 'available').length;
    const soldCount = allPlots.filter(p => p.status === 'sold').length;
    const bookedCount = allPlots.filter(p => p.status === 'booked').length;

    return (
      <section className="py-20 px-4 bg-gradient-to-b from-[#EDE9E0] to-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#8F8431] font-semibold tracking-wider uppercase">Site Layout</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C3A2A] mt-2 mb-4">
                Master Plan – <span className="text-[#8F8431]">Plot Map</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Interactive layout showing all available plots in our premium township. Green = Available, Red = Sold, Yellow = Booked
              </p>
            </motion.div>
          </div>

          {/* Stats Bar */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="bg-green-100 px-6 py-3 rounded-2xl flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              <span className="font-semibold text-green-700">{availableCount} Available</span>
            </div>
            <div className="bg-red-100 px-6 py-3 rounded-2xl flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-red-500"></div>
              <span className="font-semibold text-red-700">{soldCount} Sold</span>
            </div>
            <div className="bg-yellow-100 px-6 py-3 rounded-2xl flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
              <span className="font-semibold text-yellow-700">{bookedCount} Booked</span>
            </div>
          </div>

          {/* Filter Buttons for Map */}
          <div className="flex justify-center gap-3 mb-8">
            {['all', 'available', 'sold', 'booked'].map((filterType) => (
              <button
                key={filterType}
                onClick={() => setMapFilter(filterType)}
                className={`px-6 py-2 rounded-xl font-semibold transition-all capitalize ${mapFilter === filterType
                  ? 'bg-[#8F8431] text-white shadow-md'
                  : 'bg-[#EDE9E0] text-[#2C3A2A] hover:bg-[#E5E0D0] shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]'
                  }`}
              >
                {filterType === 'all' ? 'All Plots' : `${filterType} Plots`}
              </button>
            ))}
          </div>

          {/* Plot Grid - Like a real estate site map */}
          <div className="bg-[#EDE9E0] p-6 rounded-3xl shadow-[20px_20px_40px_#d4c9b8,-20px_-20px_40px_#ffffff] overflow-x-auto">
            {/* Row Labels (A-J) */}
            <div className="flex mb-2">
              <div className="w-12"></div>
              {[...Array(8)].map((_, col) => (
                <div key={col} className="flex-1 text-center font-bold text-[#8F8431] text-sm">
                  Col {col + 1}
                </div>
              ))}
            </div>

            {/* Grid */}
            <div className="space-y-2">
              {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'].map((rowLetter, rowIdx) => (
                <div key={rowIdx} className="flex items-center gap-2">
                  {/* Row Label */}
                  <div className="w-12 font-bold text-[#8F8431] text-center bg-[#E5E0D0] rounded-xl py-2">
                    {rowLetter}
                  </div>

                  {/* Plot Cells */}
                  <div className="flex-1 grid grid-cols-8 gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((colNum) => {
                      const plot = allPlots.find(p => p.row + 1 === rowIdx + 1 && p.col + 1 === colNum);
                      if (!plot) {
                        return <div key={colNum} className="aspect-square rounded-xl bg-gray-200 opacity-30"></div>;
                      }

                      if (mapFilter !== 'all' && plot.status !== mapFilter) {
                        return <div key={colNum} className="aspect-square rounded-xl bg-gray-200 opacity-30"></div>;
                      }

                      return (
                        <motion.button
                          key={colNum}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSelectedPlot(plot)}
                          className={`
                            relative group aspect-square rounded-xl 
                            ${getStatusColor(plot.status)} 
                            shadow-md transition-all duration-200
                            flex flex-col items-center justify-center
                            overflow-hidden
                          `}
                        >
                          <span className="text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition">
                            {plot.number}
                          </span>
                          <span className="text-white text-[10px] opacity-0 group-hover:opacity-100 transition">
                            {getStatusText(plot.status)}
                          </span>

                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black/80 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none z-20">
                            Plot {plot.number}: {getStatusText(plot.status)}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="mt-6 pt-4 border-t border-[#D4C9B8] flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span>Available for Booking</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span>Sold / Booked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-500"></div>
                <span>Under Booking</span>
              </div>
              <div className="flex items-center gap-2">
                <FaMapMarkerAlt className="text-[#8F8431]" />
                <span>Corner Plot (Premium)</span>
              </div>
            </div>
          </div>

          {/* Selected Plot Details Modal */}
          {selectedPlot && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedPlot(null)}
            >
              <div
                className="bg-[#EDE9E0] rounded-3xl p-6 max-w-md w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-[#2C3A2A]">Plot {selectedPlot.number}</h3>
                  <button onClick={() => setSelectedPlot(null)} className="text-gray-500 hover:text-red-500 text-2xl">&times;</button>
                </div>

                <div className={`w-full h-2 rounded-full mb-4 ${selectedPlot.status === 'available' ? 'bg-green-500' : selectedPlot.status === 'sold' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between py-2 border-b border-[#D4C9B8]">
                    <span className="text-gray-600">Status</span>
                    <span className={`font-semibold capitalize ${selectedPlot.status === 'available' ? 'text-green-600' : selectedPlot.status === 'sold' ? 'text-red-600' : 'text-yellow-600'}`}>
                      {selectedPlot.status}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#D4C9B8]">
                    <span className="text-gray-600">Area</span>
                    <span className="font-semibold text-[#2C3A2A]">{selectedPlot.area}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#D4C9B8]">
                    <span className="text-gray-600">Price</span>
                    <span className="font-semibold text-[#8F8431] text-xl">{selectedPlot.price}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#D4C9B8]">
                    <span className="text-gray-600">Dimensions</span>
                    <span className="font-semibold text-[#2C3A2A]">{selectedPlot.dimensions}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#D4C9B8]">
                    <span className="text-gray-600">Facing</span>
                    <span className="font-semibold text-[#2C3A2A]">{selectedPlot.facing}</span>
                  </div>
                </div>

                {selectedPlot.status === 'available' && (
                  <div className="flex gap-3">
                    <Link to="/contact-us" className="flex-1 bg-[#8F8431] text-white py-3 rounded-xl font-semibold text-center hover:bg-[#6B5F23] transition">
                      Book Now
                    </Link>
                    <button className="px-4 bg-[#E5E0D0] rounded-xl text-[#2C3A2A] hover:bg-[#D4C9B8] transition">
                      <FaWhatsapp size={20} />
                    </button>
                  </div>
                )}
                {selectedPlot.status !== 'available' && (
                  <button className="w-full bg-gray-400 text-white py-3 rounded-xl font-semibold cursor-not-allowed">
                    Not Available
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-[#EDE9E0]">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Backgrounds */}
        <video
          key={currentBgIndex}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          src={currentBgIndex === 0 ? "/video1.mp4" : "/video2.mp4"}
          autoPlay
          muted
          loop
          playsInline
        />

        <div className="absolute inset-0 bg-gradient-to-r from-[#021437]/50 to-[#021437]/50" />

        <div className="relative z-10 max-w-5xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-[#8F8431] text-white px-6 py-2 rounded-3xl mb-6 text-sm font-semibold tracking-widest shadow-lg animate-pulse">
              PREMIUM REAL ESTATE NETWORK
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-2xl">
              Find Your <span className="text-[#C7C091]">Dream Home</span>
              <br />
              <span className="text-3xl md:text-4xl">in the Heart of Perfection</span>
            </h1>

            <p className="text-xl text-[#C7C091] max-w-2xl mx-auto mb-10 drop-shadow-lg">
              Discover exclusive properties and investment opportunities with complete transparency, legal security, and unmatched luxury.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/plot-availability"
                className="px-10 py-4 bg-[#8F8431] text-white font-semibold rounded-2xl shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] active:shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E] hover:scale-105 transition-all flex items-center justify-center gap-3 text-lg"
              >
                Browse Properties
                <FaArrowRight />
              </Link>
              <Link
                to="/register"
                className="px-10 py-4 bg-[#EDE9E0] text-[#2C3A2A] font-semibold rounded-2xl shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] hover:shadow-[inset_4px_4px_10px_#d4c9b8,inset_-4px_-4px_10px_#ffffff] transition-all flex items-center justify-center gap-3 text-lg"
              >
                Join as Partner
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 relative -mt-10 z-20">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#EDE9E0] p-8 rounded-3xl text-center shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0] hover:shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] transition-all"
            >
              <stat.icon className="text-4xl text-[#8F8431] mx-auto mb-4" />
              <h3 className="text-4xl font-bold text-[#2C3A2A] mb-1">{stat.number}</h3>
              <p className="text-[#5F6A5A]">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Enhanced Property Grid Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#8F8431] font-semibold tracking-wider uppercase">Our Portfolio</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C3A2A] mt-2 mb-4">
                Premium <span className="text-[#8F8431]">Plots & Properties</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Discover handpicked properties in prime locations with complete legal assurance and world-class amenities
              </p>
            </motion.div>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-10">
            <div className="flex gap-3 bg-[#EDE9E0] p-2 rounded-2xl shadow-[inset_4px_4px_10px_#d4c9b8,inset_-4px_-4px_10px_#ffffff]">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-6 py-2 rounded-xl font-semibold transition-all ${filterStatus === 'all' ? 'bg-[#8F8431] text-white shadow-md' : 'text-[#2C3A2A] hover:bg-[#E5E0D0]'}`}
              >
                All Properties
              </button>
              <button
                onClick={() => setFilterStatus('available')}
                className={`px-6 py-2 rounded-xl font-semibold transition-all ${filterStatus === 'available' ? 'bg-[#8F8431] text-white shadow-md' : 'text-[#2C3A2A] hover:bg-[#E5E0D0]'}`}
              >
                <span className="flex items-center gap-2">
                  <FaCheckCircle /> Available
                </span>
              </button>
              <button
                onClick={() => setFilterStatus('sold')}
                className={`px-6 py-2 rounded-xl font-semibold transition-all ${filterStatus === 'sold' ? 'bg-[#8F8431] text-white shadow-md' : 'text-[#2C3A2A] hover:bg-[#E5E0D0]'}`}
              >
                <span className="flex items-center gap-2">
                  <FaTimesCircle /> Sold
                </span>
              </button>
            </div>

            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by location or project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-3 rounded-2xl bg-[#EDE9E0] shadow-[inset_4px_4px_10px_#d4c9b8,inset_-4px_-4px_10px_#ffffff] border-none focus:outline-none focus:ring-2 focus:ring-[#8F8431] w-80"
              />
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex justify-end gap-2 mb-6">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-[#8F8431] text-white' : 'bg-[#EDE9E0] text-[#2C3A2A] shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]'}`}
            >
              <FaThLarge size={20} />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`p-2 rounded-xl transition-all ${viewMode === 'map' ? 'bg-[#8F8431] text-white' : 'bg-[#EDE9E0] text-[#2C3A2A] shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]'}`}
            >
              <FaEye size={20} />
            </button>
          </div>

          {/* Conditionally Render Grid or Map */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8F8431]"></div>
            </div>
          ) : (
            viewMode === 'grid' ? (
              <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory">
                {filteredProperties.map((property, index) => (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex-shrink-0 w-80 md:w-96 snap-start group bg-[#EDE9E0] rounded-3xl overflow-hidden shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] hover:shadow-[15px_15px_30px_#d4c9b8,-15px_-15px_30px_#ffffff] transition-all duration-300"
                  >
                    {/* Image Container */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={property.photos[0]}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${property.status === 'available'
                            ? 'bg-green-500 text-white'
                            : 'bg-red-500 text-white'
                          }`}>
                          {property.status === 'available' ? 'Available' : 'Sold'}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-[#8F8431] text-white px-3 py-1 rounded-full text-xs font-bold">
                          {property.badge}
                        </span>
                      </div>
                      {property.discount && (
                        <div className="absolute bottom-4 left-4">
                          <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                            🔥 {property.discount}
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-4 gap-3">
                        <button className="bg-white p-2 rounded-full hover:bg-[#8F8431] hover:text-white transition">
                          <FaRegHeart />
                        </button>
                        <button className="bg-white p-2 rounded-full hover:bg-[#8F8431] hover:text-white transition">
                          <FaShareAlt />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-bold text-[#2C3A2A] group-hover:text-[#8F8431] transition">
                          {property.title}
                        </h3>
                        <p className="text-2xl font-bold text-[#8F8431]">{property.price}</p>
                      </div>

                      <div className="flex items-center gap-2 text-gray-500 mb-4">
                        <FaMapMarkerAlt className="text-[#8F8431]" />
                        <span className="text-sm">{property.location}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-[#D4C9B8]">
                        <div className="flex items-center gap-2">
                          <FaVectorSquare className="text-[#8F8431]" />
                          <span className="text-sm">{property.area}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaBuilding className="text-[#8F8431]" />
                          <span className="text-sm">{property.type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <span className="text-sm">Facing: {property.facing}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <span className="text-sm">{property.widthDim} x {property.depthDim}</span>
                        </div>
                      </div>

                      {/* Amenities */}
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-[#2C3A2A] mb-2">Amenities:</p>
                        <div className="flex flex-wrap gap-2">
                          {property.features.map((amenity, i) => (
                            <span key={i} className="text-xs bg-[#E5E0D0] px-2 py-1 rounded-full text-[#2C3A2A]">
                              {amenity}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Nearby Places */}
                      <div className="mb-5">
                        <p className="text-sm font-semibold text-[#2C3A2A] mb-2">Nearby:</p>
                        <div className="flex flex-wrap gap-2">
                          {property.nearby.map((place, i) => (
                            <span key={i} className="text-xs bg-[#8F8431]/10 px-2 py-1 rounded-full text-[#8F8431]">
                              {place}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        {property.status === 'available' ? (
                          <>
                            <Link
                              to="/contact-us"
                              className="flex-1 bg-[#8F8431] text-white py-2 rounded-xl font-semibold text-center hover:bg-[#6B5F23] transition flex items-center justify-center gap-2"
                            >
                              <FaWhatsapp /> Enquire
                            </Link>
                            <button className="px-4 bg-[#E5E0D0] rounded-xl text-[#2C3A2A] hover:bg-[#D4C9B8] transition">
                              <FaPhoneAlt />
                            </button>
                          </>
                        ) : (
                          <button className="flex-1 bg-gray-400 text-white py-2 rounded-xl font-semibold cursor-not-allowed flex items-center justify-center gap-2">
                            <FaTimesCircle /> Sold Out
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

            ) : (
              <PlotMapGrid plots={plots} />
            )
          )}

          {filteredProperties.length === 0 && viewMode === 'grid' && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No properties found matching your criteria.</p>
            </div>
          )}

          {/* View All Button */}
          {viewMode === 'grid' && (
            <div className="text-center mt-12">
              <Link
                to="/plot-availability"
                className="inline-flex items-center gap-2 px-8 py-3 bg-[#8F8431] text-white rounded-xl font-semibold hover:bg-[#6B5F23] transition shadow-lg"
              >
                View All Properties <FaArrowRight />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Mission / Vision / Philosophy */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#8F8431] font-semibold tracking-wider uppercase">Our Ethos</span>
            <h2 className="text-4xl font-bold text-[#2C3A2A] mt-2">The Foundation of Trust</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Our Mission", icon: FaStar, desc: "To deliver exceptional real estate solutions with transparency and integrity, creating value for every stakeholder." },
              { title: "Our Vision", icon: FaChartLine, desc: "To become India's most trusted real estate network, empowering thousands of families to achieve their dream of homeownership." },
              { title: "Our Philosophy", icon: FaHandshake, desc: "Building long-term relationships based on trust, respect, and mutual growth, where every client becomes a family member." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-[#EDE9E0] p-10 rounded-3xl shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0] hover:shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] transition-all"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#8F8431] flex items-center justify-center mb-6">
                  <item.icon className="text-3xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#2C3A2A] mb-4">{item.title}</h3>
                <p className="text-[#385532] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us with Enhanced Icons */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#8F8431] font-semibold tracking-wider uppercase">Advantages</span>
            <h2 className="text-4xl font-bold text-[#2C3A2A] mt-2">Why Choose Dharaa Pariwar?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">Experience the difference with our unmatched services and partner benefits</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Proven Business Model", icon: FaChartLine, desc: "A successful system that has helped hundreds achieve financial freedom and wealth creation." },
              { title: "Comprehensive Training", icon: FaUserTie, desc: "Industry-leading training and ongoing support from experts to ensure your success." },
              { title: "Exclusive Properties", icon: FaHome, desc: "Access to premium listings and off-market properties not available elsewhere." },
              { title: "Marketing Support", icon: FaMedal, desc: "Professional tools, marketing materials, and digital campaigns to boost your sales." },
              { title: "Legal Security", icon: FaShieldAlt, desc: "Full compliance, transparent processes, and complete legal due diligence on every property." },
              { title: "Community Support", icon: FaUsers, desc: "Strong network of successful partners, regular meetups, and collaborative growth." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-[#EDE9E0] p-8 rounded-3xl shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0] hover:shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] transition-all group"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#8F8431] flex items-center justify-center mb-6 group-hover:scale-110 transition">
                  <item.icon className="text-3xl text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#2C3A2A] mb-3">{item.title}</h3>
                <p className="text-[#385532]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#8F8431] font-semibold tracking-wider uppercase">Testimonials</span>
            <h2 className="text-4xl font-bold text-[#2C3A2A] mt-2">What Our Clients Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Rahul Mehta", role: "Homeowner", text: "Dharaa Pariwar made my dream of owning a home come true. Their transparency and support throughout the process was exceptional.", rating: 5 },
              { name: "Priya Sharma", role: "Investor", text: "The best real estate investment I've made. Their team's guidance helped me choose the perfect property with great returns.", rating: 5 },
              { name: "Amit Kumar", role: "Partner", text: "Joining as a partner was the best decision. The training and support system is incredible. My income has tripled!", rating: 5 }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-[#EDE9E0] p-8 rounded-3xl shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff]"
              >
                <div className="flex gap-1 text-yellow-500 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => <FaStar key={i} />)}
                </div>
                <p className="text-[#2C3A2A] italic mb-6">"{testimonial.text}"</p>
                <div className="border-t border-[#D4C9B8] pt-4">
                  <p className="font-bold text-[#2C3A2A]">{testimonial.name}</p>
                  <p className="text-sm text-[#8F8431]">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#021437] to-[#0A2A4A] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Build Your Future?</h2>
            <p className="text-[#C7C091] text-lg mb-8">Join India's fastest growing real estate network today and unlock unlimited opportunities</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-10 py-4 bg-[#8F8431] text-white font-semibold rounded-2xl shadow-lg hover:scale-105 transition-all inline-flex items-center gap-2"
              >
                Become a Partner Now <FaArrowRight />
              </Link>
              <Link
                to="/contact-us"
                className="px-10 py-4 border-2 border-[#8F8431] text-white font-semibold rounded-2xl hover:bg-[#8F8431] transition-all inline-flex items-center gap-2"
              >
                Schedule a Call <FaPhoneAlt />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;